import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import { FaExternalLinkAlt, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './CurrentLocation.module.scss';
import { TrackingItemDto } from 'api/tracking/models/TrackingItemDto';
import { addressFormat, useGeoLocator } from 'common/services/GeoLocator';
import TrackingService from 'api/tracking/TrackingService';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { DATE_TIME_FORMAT_DEFAULT, googleMapsQuery } from 'Config';
import PrivacyStatusBadge from 'common/components/privacyStatusBadge/PrivacyStatusBadge';

type Props = {
    position?: TrackingItemDto;
    privacyStatus?: { isPrivate: boolean; isPartial?: boolean; type?: string; activatedByName?: string | null; activatedAt?: string | null; resumesAt?: string | null };
};

const CurrentLocation: React.FC<Props> = ({ position, privacyStatus }: Props) => {
    const { t } = useTranslation();
    // Se o backend já mandou a morada (viatura parada), não geocodifica no cliente.
    const knownAddress = position?.address;
    const { isLoading, error, data } = useGeoLocator(position?.latitude ?? 0, position?.longitude ?? 0, null, !knownAddress);

    // Parado sem morada na BD: gravar a morada geocodada no fim da última viagem (end_address),
    // para o próximo load servir da BD sem geocodificar de novo.
    useEffect(() => {
        if (!knownAddress && position?.lastTripId && data?.display_name) {
            const formatted = addressFormat(data, position?.latitude ?? 0, position?.longitude ?? 0);
            TrackingService.updateGpsAddress(position.lastTripId, formatted, true).catch(() => { /* noop */ });
        }
    }, [data, position?.lastTripId, knownAddress]);

    if (privacyStatus?.isPrivate) {
        return <PrivacyStatusBadge
            type={privacyStatus.type}
            activatedByName={privacyStatus.activatedByName}
            activatedAt={privacyStatus.activatedAt}
            resumesAt={privacyStatus.resumesAt}
        />;
    }

    if (!position) return null;

    const isIgnitionOn = (item?: TrackingItemDto) => {
        return item?.ignition ?? false;
    }

    const link = googleMapsQuery(position?.latitude, position?.longitude);

    const renderAddress = (d: any) => <div>
            <div className={`${styles.container} ${isIgnitionOn(position) ? styles.containerOn : styles.containerOff}`} data-tip={t('vehicles.list.current_postion')}>
            <div className={styles.pin}>
                <FaMapMarkerAlt />
            </div>
            <div className={styles.content}>
                { <div className={styles.address}>
                    {knownAddress ? knownAddress : addressFormat(d, position?.latitude ?? 0, position?.longitude ?? 0)}
                    <div className={styles.labelAddress}>
                        <a onClick={event => { event.stopPropagation(); window.open(link, '_blank'); }}>{t('alarms.notifications_tab.table.open_location')}
                            <span className={styles.linkIcon}>
                                <FaExternalLinkAlt/>
                            </span>
                        </a>
                    </div>
                </div> }
                <div className={styles.info}>
                    {isIgnitionOn(position) ? t('vehicles.list.ignition_on') : t('vehicles.list.ignition_off')}
                    &nbsp;&nbsp;&#x2022;&nbsp;&nbsp;
                    {t('vehicles.list.last_update')}&nbsp;
                    <DateFormat value={position?.date} format={DATE_TIME_FORMAT_DEFAULT.toString()} />
                </div>
            </div>
        </div>
        <ReactTooltip />
    </div>

    if (isLoading) return renderAddress(null);
    if (error) console.log('An error occurred while fetching the user data ', error);
    return renderAddress(data);
}

export default CurrentLocation;
